import React, { useEffect, useMemo, useState } from 'react';
import AdminHeader from '../components/AdminHeader';
import Select from 'react-select';
import { toast } from 'react-toastify';

const SIZE_ORDER = ['M', 'L', 'XL', '2XL'];

const emptyRow = (size) => ({
    size,
    totalLength: '',
    sleeveLength: '',
    sleeveBottom: '',
    chestWidth: ''
});

const toNullableNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

function Charts() {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [rows, setRows] = useState(SIZE_ORDER.map(emptyRow));
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/getCategories/BLACKBATON_2526`);
                const data = await response.json();
                setCategories(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching categories:', error);
                toast.error('Failed to load categories');
            }
        };

        fetchCategories();
    }, [apiBaseUrl]);

    useEffect(() => {
        const fetchChart = async () => {
            if (!selectedCategory?.value) return;
            setLoading(true);
            try {
                const response = await fetch(
                    `${apiBaseUrl}/getSizeChart/BLACKBATON_2526?categoryId=${selectedCategory.value}`
                );
                const data = await response.json();
                const chartRows = (data?.data || []);

                const merged = SIZE_ORDER.map((size) => {
                    const found = chartRows.find((row) => String(row.SizeLabel).toUpperCase() === size);
                    return {
                        size,
                        totalLength: found?.TotalLength ?? '',
                        sleeveLength: found?.SleeveLength ?? '',
                        sleeveBottom: found?.SleeveBottom ?? '',
                        chestWidth: found?.ChestWidth ?? ''
                    };
                });
                setRows(merged);
            } catch (error) {
                console.error('Error fetching size chart:', error);
                toast.error('Failed to load chart');
                setRows(SIZE_ORDER.map(emptyRow));
            } finally {
                setLoading(false);
            }
        };

        fetchChart();
    }, [selectedCategory, apiBaseUrl]);

    const categoryOptions = useMemo(
        () => categories.map((category) => ({ value: category.Id, label: category.Name })),
        [categories]
    );

    const onFieldChange = (size, field, value) => {
        setRows((prev) =>
            prev.map((row) => (row.size === size ? { ...row, [field]: value } : row))
        );
    };

    const handleSave = async () => {
        if (!selectedCategory?.value) {
            toast.info('Select a category first');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                categoryId: selectedCategory.value,
                rows: rows.map((row) => ({
                    size: row.size,
                    totalLength: toNullableNumber(row.totalLength),
                    sleeveLength: toNullableNumber(row.sleeveLength),
                    sleeveBottom: toNullableNumber(row.sleeveBottom),
                    chestWidth: toNullableNumber(row.chestWidth)
                }))
            };

            const response = await fetch(`${apiBaseUrl}/upsertSizeChart/BLACKBATON_2526`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (!response.ok || !data?.success) {
                throw new Error(data?.error || 'Failed to save chart');
            }

            toast.success('Chart saved successfully');
        } catch (error) {
            console.error('Error saving chart:', error);
            toast.error(error.message || 'Failed to save chart');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="w-full min-h-screen bg-[#f7f7f7]">
            <AdminHeader />
            <div className="max-w-6xl mx-auto px-3 md:px-6 py-6">
                <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6">
                    <h1 className="text-xl md:text-2xl font-bold font-montserrat text-left">Size Charts</h1>
                    <p className="text-sm text-gray-500 text-left mt-1">Select category and manage M, L, XL, 2XL chart values.</p>

                    <div className="mt-5 max-w-md text-left">
                        <span className="text-sm font-semibold">Category</span>
                        <Select
                            value={selectedCategory}
                            onChange={setSelectedCategory}
                            options={categoryOptions}
                            className="mt-2"
                        />
                    </div>

                    {selectedCategory && (
                        <div className="mt-6 overflow-x-auto">
                            <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="text-left px-3 py-2 text-sm font-bold">Size</th>
                                        <th className="text-left px-3 py-2 text-sm font-bold">Total Length (in)</th>
                                        <th className="text-left px-3 py-2 text-sm font-bold">Sleeve Length (in)</th>
                                        <th className="text-left px-3 py-2 text-sm font-bold">Sleeve Bottom (in)</th>
                                        <th className="text-left px-3 py-2 text-sm font-bold">Chest Width (in)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.size} className="border-t border-gray-200">
                                            <td className="px-3 py-2 text-sm font-semibold">{row.size}</td>
                                            <td className="px-3 py-2">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={row.totalLength}
                                                    onChange={(e) => onFieldChange(row.size, 'totalLength', e.target.value)}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                                    disabled={loading}
                                                />
                                            </td>
                                            <td className="px-3 py-2">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={row.sleeveLength}
                                                    onChange={(e) => onFieldChange(row.size, 'sleeveLength', e.target.value)}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                                    disabled={loading}
                                                />
                                            </td>
                                            <td className="px-3 py-2">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={row.sleeveBottom}
                                                    onChange={(e) => onFieldChange(row.size, 'sleeveBottom', e.target.value)}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                                    disabled={loading}
                                                />
                                            </td>
                                            <td className="px-3 py-2">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={row.chestWidth}
                                                    onChange={(e) => onFieldChange(row.size, 'chestWidth', e.target.value)}
                                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                                    disabled={loading}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={handleSave}
                                    disabled={saving || loading}
                                    className="px-5 py-2 bg-black text-white rounded-lg text-sm font-semibold disabled:opacity-60"
                                >
                                    {saving ? 'Saving...' : 'Save Chart'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Charts;
